'use client';

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';

import { cn, createTestIdHelper } from '../../lib/utils';

import {
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from './alert-dialog';
import { buttonVariants, type ButtonProps } from './button';

const testId = createTestIdHelper('confirm-dialog');

/**
 * The app's standard confirm modal: a title, a consequence line, an optional body, and a
 * Cancel/Action row.
 *
 * It exists because four dialogs across `heartplus` and the Engage+ MFE had already converged on
 * exactly this chrome by copy-paste — down to identical class strings — and two of them were a
 * file-level fork of each other. This is that shape with one owner. Every measurement here is the
 * one those dialogs already shipped; nothing was restyled or retokenised in the extraction.
 *
 * It composes `AlertDialog` rather than replacing it, so Radix keeps owning focus trapping, the
 * escape/overlay dismissal, the ENTER animation and the `aria-labelledby`/`describedby` wiring
 * between Title, Description and Content. Not the EXIT animation: {@link ConfirmDialogContent}
 * unmounts on close, so `data-[state=closed]:animate-out` never runs and the dialog disappears
 * instantly. That is what all four call sites already did by hand, and the mount gate is worth
 * more here than the fade — but it is a real trade, not an oversight.
 *
 * Three things the stock `AlertDialog*` slots could not do, which is why all four call sites
 * bypassed them:
 *
 * 1. **`AlertDialogAction` hardcodes `buttonVariants()`** with no variant escape hatch, so a
 *    destructive confirm had to be a hand-rolled `<Button>`. {@link ConfirmDialogAction} takes
 *    `variant`/`size`.
 * 2. **`AlertDialogPrimitive.Action` always closes on click**, and that is the wrong default
 *    here. Every confirm in this codebase runs work that owns its own dismissal, and a
 *    Radix-driven close ALSO fires the caller's `onOpenChange(false)` — which, in the shape all
 *    of them use (`onOpenChange={(next) => !next && onCancel()}`), runs the cancel path on top
 *    of the confirm. So {@link ConfirmDialogAction} does not close by default; `closeOnClick`
 *    opts back in.
 * 3. **`AlertDialogHeader` centres on mobile** (`text-center sm:text-left`) and
 *    `AlertDialogFooter`'s `space-x-2` / `flex-col-reverse` fight the intended row.
 *
 * Generic and context-free by design: no feature logic, no copy. Callers own every label.
 */
interface ConfirmDialogContextValue {
  open: boolean;
}

const ConfirmDialogContext =
  React.createContext<ConfirmDialogContextValue | null>(null);

function useConfirmDialogContext() {
  const ctx = React.useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('ConfirmDialog.* must be used within <ConfirmDialog>');
  }
  return ctx;
}

export interface ConfirmDialogProps extends Omit<
  React.ComponentProps<typeof AlertDialogPrimitive.Root>,
  'open' | 'defaultOpen'
> {
  /**
   * Required, unlike Radix's optional `open`. This shell is deliberately controlled-only:
   * {@link ConfirmDialogContent} mounts its subtree ONLY while open, and an uncontrolled dialog
   * gives it nothing to gate on.
   */
  open: boolean;
}

function ConfirmDialog({ open, children, ...props }: ConfirmDialogProps) {
  const value = React.useMemo(() => ({ open }), [open]);

  return (
    <ConfirmDialogContext.Provider value={value}>
      <AlertDialogPrimitive.Root open={open} {...props}>
        {children}
      </AlertDialogPrimitive.Root>
    </ConfirmDialogContext.Provider>
  );
}

/**
 * The modal surface — 448px, 12px radius, no padding of its own (each section pads itself).
 *
 * Mount-gates its children on `open`. That is a guarantee, not a convenience: a closed confirm
 * renders NOTHING, so a body that reads on mount reads afresh every time the dialog opens rather
 * than answering from a subscription that stayed alive while hidden. All four call sites were
 * already writing `{open ? … : null}` by hand; making it the shell's job is what stops the fifth
 * from forgetting.
 */
function ConfirmDialogContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogContent>) {
  const { open } = useConfirmDialogContext();

  if (!open) {
    return null;
  }

  return (
    <AlertDialogContent
      data-slot="confirm-dialog-content"
      data-testid={testId('root')}
      className={cn('max-w-md gap-0 overflow-hidden rounded-xl p-0', className)}
      {...props} // caller may override data-testid to preserve feature selectors
    >
      {children}
    </AlertDialogContent>
  );
}

/** Title, description and any body, on one 8px rhythm. Left-aligned at every width. */
function ConfirmDialogHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="confirm-dialog-header"
      className={cn('flex flex-col gap-2 px-6 py-5', className)}
      {...props}
    />
  );
}

function ConfirmDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogTitle>) {
  return (
    <AlertDialogTitle
      data-slot="confirm-dialog-title"
      className={cn('text-lg font-medium text-gray-800', className)}
      {...props}
    />
  );
}

/**
 * The consequence line.
 *
 * `text-gray-500` — NOT `text-muted-foreground`, and that is deliberate. `--muted-foreground`
 * resolves to `gray-400` in BOTH themes (~2.6:1 on white), below the WCAG AA floor for the one
 * sentence stating what a confirm is about to do. `gray-500` is ~4.6:1 in light and, through the
 * inverting `--themed-gray-500`, `gray-400` on the near-black dark surface. Hierarchy against the
 * title comes from size and weight, not from lightening this further.
 */
function ConfirmDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogDescription>) {
  return (
    <AlertDialogDescription
      data-slot="confirm-dialog-description"
      className={cn('text-sm font-normal leading-5 text-gray-500', className)}
      {...props}
    />
  );
}

/**
 * Optional content between the description and the actions — a summary, a form, a warning.
 *
 * A sibling of Title/Description inside the header, so the header's `gap-2` plus this `pt-2` give
 * the 16px separation the existing dialogs render.
 */
function ConfirmDialogBody({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="confirm-dialog-body"
      className={cn('pt-2', className)}
      {...props}
    />
  );
}

/** Right-aligned action row. `gap-3`, and it does not reverse or stack on small widths. */
function ConfirmDialogFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="confirm-dialog-footer"
      className={cn('flex items-center justify-end gap-3 px-6 pb-5', className)}
      {...props}
    />
  );
}

/**
 * Dismisses natively (Radix `Cancel`). Kept first in the DOM so Radix's auto-focus lands here —
 * the non-committal choice gets focus by default, which matters most on the destructive confirms.
 *
 * Quiet by construction: the outline variant's border is dropped for a filled neutral, so the one
 * emphasised control in the row is always the Action.
 */
function ConfirmDialogCancel({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogCancel>) {
  return (
    <AlertDialogCancel
      data-slot="confirm-dialog-cancel"
      data-testid={testId('cancel')}
      className={cn(
        'm-0 border-0 bg-gray-100 text-gray-600 hover:bg-gray-200',
        className,
      )}
      {...props} // caller may override data-testid to preserve feature selectors
    />
  );
}

export interface ConfirmDialogActionProps
  // `asChild` is deliberately NOT part of this API. Radix's Action supports it, but the
  // `closeOnClick={false}` branch is a plain <button> that cannot — it would forward `asChild`
  // to the DOM and render the child INSIDE the button instead of replacing it. Excluding it
  // makes that a compile error rather than a runtime warning plus nested interactive elements.
  extends
    Omit<React.ComponentProps<typeof AlertDialogPrimitive.Action>, 'asChild'>,
    Pick<ButtonProps, 'variant' | 'size'> {
  /**
   * Whether clicking dismisses the dialog. Default **`false`** — the caller owns closing.
   *
   * The default is the safe one on purpose. Opting in renders Radix's `Action`, which dismisses
   * natively and therefore also fires the caller's `onOpenChange(false)`; if that handler routes
   * to a cancel/reset path (the shape every call site here uses), a single click runs BOTH the
   * confirm and the cancel. Turn it on only when dismissal IS the whole reaction to the click
   * and `onOpenChange` does nothing a confirm should not trigger.
   *
   * Leaving it off means a handler that never closes leaves the dialog open — visible and
   * fixable, unlike a silent double-fire.
   */
  closeOnClick?: boolean;
}

/**
 * The committing action. Neutral by default so the component stays agnostic; a destructive call
 * site opts in with `variant="destructive"`. The label is caller-owned via children — and on a
 * confirm that acts on a set, it should carry the count ("Send 232 requests", not "Confirm"),
 * because the number is the thing being approved.
 */
function ConfirmDialogAction({
  className,
  variant = 'default',
  size = 'sm',
  closeOnClick = false,
  ...props
}: ConfirmDialogActionProps) {
  const classes = cn(buttonVariants({ variant, size }), className);

  if (!closeOnClick) {
    return (
      <button
        type="button"
        data-slot="confirm-dialog-action"
        data-testid={testId('action')}
        className={classes}
        {...props}
      />
    );
  }

  return (
    <AlertDialogPrimitive.Action
      // Explicit on BOTH branches: a <button> with no `type` defaults to `submit`. The content
      // is portalled today so it cannot sit inside a form, but letting the two paths differ on
      // this leaves a form-submitting action one refactor away.
      type="button"
      data-slot="confirm-dialog-action"
      data-testid={testId('action')}
      className={classes}
      {...props} // caller may override data-testid to preserve feature selectors
    />
  );
}

export {
  ConfirmDialog,
  ConfirmDialogAction,
  ConfirmDialogBody,
  ConfirmDialogCancel,
  ConfirmDialogContent,
  ConfirmDialogDescription,
  ConfirmDialogFooter,
  ConfirmDialogHeader,
  ConfirmDialogTitle,
};
